import {
  isSameStringArray,
  resolveUpdater,
} from "@src/components/ui-kit/DataTable/Body/helpers";
import {
  EXPANDER_COLUMN_ID,
  SELECTION_COLUMN_ID,
} from "@src/components/ui-kit/DataTable/Body/hooks/columns";
import type { DataTableBodyStore } from "@src/components/ui-kit/DataTable/Body/store";
import type {
  BodyProps,
  BodyRow,
} from "@src/components/ui-kit/DataTable/types";
import type { Updater } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

interface UseTableBodyOrderArgs<Row extends BodyRow>
  extends Pick<BodyProps<Row, unknown>, "enableColumnOrdering"> {
  tanstackBodyStore: DataTableBodyStore;
  isRowSelectionEnabled: boolean;
  hasSubRows: boolean;
  hideExpandColumn: boolean;
  dataColumnIds: string[];
  dataColumnIdsSet: Set<string>;
}
export function useTableBodyOrder<Row extends BodyRow>({
  enableColumnOrdering,
  tanstackBodyStore,
  isRowSelectionEnabled,
  hasSubRows,
  hideExpandColumn,
  dataColumnIds,
  dataColumnIdsSet,
}: UseTableBodyOrderArgs<Row>) {
  const dataColumnOrder = useMemo(() => {
    if (
      !enableColumnOrdering ||
      tanstackBodyStore.dataColumnOrder.length === 0
    ) {
      return dataColumnIds;
    }

    const nextOrder = tanstackBodyStore.dataColumnOrder.filter((id) =>
      dataColumnIdsSet.has(id),
    );

    for (const id of dataColumnIds) {
      if (!nextOrder.includes(id)) {
        nextOrder.push(id);
      }
    }

    return nextOrder;
  }, [
    dataColumnIds,
    dataColumnIdsSet,
    enableColumnOrdering,
    tanstackBodyStore.dataColumnOrder,
  ]);

  const columnOrder = useMemo(() => {
    const fixedColumnIds: string[] = [];

    if (isRowSelectionEnabled) {
      fixedColumnIds.push(SELECTION_COLUMN_ID);
    }

    if (hasSubRows && !hideExpandColumn) {
      fixedColumnIds.push(EXPANDER_COLUMN_ID);
    }

    return fixedColumnIds.concat(dataColumnOrder);
  }, [dataColumnOrder, hasSubRows, hideExpandColumn, isRowSelectionEnabled]);

  const hasCustomColumnOrder = !isSameStringArray(
    dataColumnOrder,
    dataColumnIds,
  );

  const onColumnOrderChange = useCallback(
    (updater: Updater<string[]>) => {
      if (!enableColumnOrdering) {
        return;
      }

      const nextOrder = resolveUpdater(updater, columnOrder);
      const nextDataColumnOrder = nextOrder.filter((id) =>
        dataColumnIdsSet.has(id),
      );

      tanstackBodyStore.setDataColumnOrder(nextDataColumnOrder);
    },
    [
      columnOrder,
      dataColumnIdsSet,
      enableColumnOrdering,
      tanstackBodyStore.setDataColumnOrder,
    ],
  );

  return {
    columnOrder,
    dataColumnOrder,
    onColumnOrderChange,
    hasCustomColumnOrder,
  };
}
